import React, { useEffect, useState, useCallback } from 'react';
import { api, formatApiError } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';
import { FieldRender } from '../../pages/PublicForm';
import { Plus, Trash2, Copy, ExternalLink, Eye, FileText, Mail, Hash, AlignLeft, ChevronsUpDown, CheckSquare, Calendar, PenTool, Heading1, Minus, Type, Phone } from 'lucide-react';

const FIELD_TYPES = [
  { type: 'text',      icon: Type,           label: 'Short text' },
  { type: 'email',     icon: Mail,           label: 'Email' },
  { type: 'phone',     icon: Phone,          label: 'Phone' },
  { type: 'number',    icon: Hash,           label: 'Number' },
  { type: 'textarea',  icon: AlignLeft,      label: 'Long text' },
  { type: 'select',    icon: ChevronsUpDown, label: 'Dropdown' },
  { type: 'radio',     icon: CheckSquare,    label: 'Radio' },
  { type: 'checkbox',  icon: CheckSquare,    label: 'Checkbox' },
  { type: 'date',      icon: Calendar,       label: 'Date' },
  { type: 'signature', icon: PenTool,        label: 'Signature' },
  { type: 'heading',   icon: Heading1,       label: 'Heading' },
  { type: 'divider',   icon: Minus,          label: 'Divider' },
];

const Forms = ({ lob }) => {
  const darkMode = useDarkMode();
  const aBg = darkMode ? lob?.accentBgDark : lob?.accentBg;
  const aTc = darkMode ? lob?.accentTcDark : lob?.accentTc;
  const [forms, setForms] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    if (!lob) return;
    try { const { data } = await api.get(`/forms?lob_id=${lob.id}`); setForms(data); }
    catch (e) { setErr(formatApiError(e)); }
  }, [lob?.id]);

  useEffect(() => { load(); }, [load]);

  // Reload selected form when forms list changes (e.g., to refresh submission count)
  useEffect(() => {
    if (selected) {
      const upd = forms.find((f) => f.id === selected.id);
      if (upd && upd.updated_at !== selected.updated_at) setSelected(upd);
    }
  }, [forms, selected]);

  return (
    <>
      <div className="page-toolbar" data-testid="forms-toolbar">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="page-title">Forms</span>
          <span className="lob-badge" style={{ background: aBg }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lob.accent, display: 'inline-block', marginRight: 4 }} />
            <span style={{ color: aTc }}>{lob.label}</span>
          </span>
        </div>
        <button className="cta-btn" style={{ background: lob.accent }} onClick={() => setShowCreate(true)} data-testid="add-form-btn">
          <Plus size={11} style={{ marginRight: 4 }} />New form
        </button>
      </div>

      {err && <div className="module-banner err">{err}</div>}

      <div className="fb-shell">
        <div className="fb-list-pane" data-testid="forms-list">
          {forms.length === 0 && (
            <div className="empty-state"><div className="ttl">No forms yet</div><div className="sub">Create your first form to capture leads.</div></div>
          )}
          {forms.map((f) => (
            <div key={f.id} className={`fb-list-row${selected?.id === f.id ? ' active' : ''}`} onClick={() => setSelected(f)} data-testid={`form-row-${f.id}`}>
              <FileText size={14} style={{ color: 'var(--text-faded)' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{f.title}</div>
                <div className="fb-list-meta">{f.type} · {(f.fields || []).length} fields · {f.submission_count} submissions</div>
              </div>
              <span className={`tag-pill ${f.is_active ? 'ok' : 'muted'}`}>{f.is_active ? 'Live' : 'Off'}</span>
            </div>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {!selected ? (
            <div className="empty-state" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <FileText size={36} style={{ opacity: 0.25 }} />
              <div className="ttl">Select a form to edit</div>
              <div className="sub">Or click <b>New form</b> to start.</div>
            </div>
          ) : (
            <FormEditor key={selected.id} form={selected} onChange={load} onClose={() => setSelected(null)} accent={lob.accent} />
          )}
        </div>
      </div>

      {showCreate && (
        <CreateFormModal lob={lob} onClose={(saved) => { setShowCreate(false); if (saved) { load(); setSelected(saved); } }} />
      )}
    </>
  );
};

const CreateFormModal = ({ lob, onClose }) => {
  const [form, setForm] = useState({ lob_id: lob.id, title: '', type: 'lead', fields: [], description: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      const starter = form.type === 'lead'
        ? [
            { id: crypto.randomUUID(), type: 'text', label: 'First name', name: 'first', required: true, options: [] },
            { id: crypto.randomUUID(), type: 'text', label: 'Last name', name: 'last', required: true, options: [] },
            { id: crypto.randomUUID(), type: 'email', label: 'Email', name: 'email', required: true, options: [] },
            { id: crypto.randomUUID(), type: 'phone', label: 'Phone', name: 'phone', options: [] },
          ]
        : form.type === 'agreement'
        ? [
            { id: crypto.randomUUID(), type: 'heading', label: 'Agreement', options: [] },
            { id: crypto.randomUUID(), type: 'text', label: 'Full name', name: 'name', required: true, options: [] },
            { id: crypto.randomUUID(), type: 'date', label: 'Date', required: true, options: [] },
            { id: crypto.randomUUID(), type: 'signature', label: 'Signature', required: true, options: [] },
          ]
        : [];
      const { data } = await api.post('/forms', { ...form, fields: starter });
      onClose(data);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay-v2" onClick={() => onClose(null)}>
      <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="create-form-modal">
        <div className="modal-title">New form</div>
        <label className="f-label">Title</label>
        <input className="f-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cf-title" />
        <label className="f-label">Type</label>
        <select className="f-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="cf-type">
          <option value="lead">Public lead-capture (gets a public URL)</option>
          <option value="internal">Internal data-entry (staff only)</option>
          <option value="agreement">Agreement / signable document</option>
        </select>
        <label className="f-label">Description (optional)</label>
        <textarea className="f-textarea" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        {err && <div className="f-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(null)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="cf-submit">{busy ? 'Creating…' : 'Create'}</button>
        </div>
      </form>
    </div>
  );
};

const FormEditor = ({ form, onChange, onClose, accent }) => {
  const [draft, setDraft] = useState(form);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState('build'); // build | preview | submissions | settings
  const [submissions, setSubmissions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => { setDraft(form); setSelectedId(null); }, [form?.id]);

  useEffect(() => {
    if (tab === 'submissions') {
      api.get(`/forms/${form.id}/submissions`).then((r) => setSubmissions(r.data)).catch(() => {});
    }
  }, [tab, form.id]);

  const save = async () => {
    setBusy(true); setErr('');
    try {
      const { lob_id, type, ...rest } = draft;
      const { data } = await api.patch(`/forms/${form.id}`, { ...rest, type });
      setSavedAt(new Date().toLocaleTimeString());
      onChange();
      setDraft(data);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  const addField = (type) => {
    const id = crypto.randomUUID();
    const base = { id, type, label: FIELD_TYPES.find((f) => f.type === type)?.label || 'Field', required: false, options: [] };
    if (type === 'select' || type === 'radio') base.options = ['Option 1', 'Option 2'];
    if (type === 'heading') base.label = 'Section heading';
    setDraft({ ...draft, fields: [...(draft.fields || []), base] });
    setSelectedId(id);
  };

  const updateField = (id, patch) => setDraft({ ...draft, fields: draft.fields.map((f) => f.id === id ? { ...f, ...patch } : f) });
  const deleteField = (id) => setDraft({ ...draft, fields: draft.fields.filter((f) => f.id !== id) });
  const moveField = (id, dir) => {
    const idx = draft.fields.findIndex((f) => f.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= draft.fields.length) return;
    const fields = [...draft.fields];
    [fields[idx], fields[j]] = [fields[j], fields[idx]];
    setDraft({ ...draft, fields });
  };

  const publicUrl = draft.public_slug ? `${window.location.origin}/f/${draft.public_slug}` : null;

  return (
    <>
      <div className="page-toolbar" style={{ borderTop: '0.5px solid var(--stroke-lighter)' }}>
        <div style={{ fontWeight: 600, fontSize: 13 }} data-testid="form-editor-title">{draft.title}</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {['build', 'preview', 'submissions', 'settings'].map((t) => (
            <button key={t} className={`crm-nav-item${tab === t ? ' active' : ''}`} style={{ color: tab === t ? '#fff' : 'var(--text-sub)', background: tab === t ? accent : 'transparent', textTransform: 'capitalize' }} onClick={() => setTab(t)} data-testid={`tab-${t}`}>{t}</button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          {publicUrl && tab !== 'settings' && (
            <button className="dp-btn" onClick={() => navigator.clipboard.writeText(publicUrl)} data-testid="copy-public-url"><Copy size={11} style={{ marginRight: 4 }} />Copy URL</button>
          )}
          {savedAt && <span style={{ fontSize: 10, color: 'var(--text-faded)' }}>Saved {savedAt}</span>}
          <button className="cta-btn" style={{ background: accent }} onClick={save} disabled={busy} data-testid="save-form-btn">{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>

      {err && <div className="module-banner err" data-testid="form-edit-error">{err}</div>}

      {tab === 'build' && (
        <div className="fb-builder">
          <div className="fb-fields-side" data-testid="fields-side">
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-faded)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 8 }}>Add field</div>
            {FIELD_TYPES.map(({ type, icon: I, label }) => (
              <button key={type} className="fb-add-btn" type="button" onClick={() => addField(type)} data-testid={`add-field-${type}`}>
                <I size={12} />{label}
              </button>
            ))}
          </div>
          <div className="fb-canvas">
            <div className="fb-canvas-card">
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{draft.title}</div>
              {draft.description && <div style={{ fontSize: 12, color: 'var(--text-faded)', marginBottom: 16 }}>{draft.description}</div>}
              {(draft.fields || []).length === 0 && (
                <div className="empty-state"><div className="ttl">No fields yet</div><div className="sub">Pick a field type from the left.</div></div>
              )}
              {(draft.fields || []).map((f) => (
                <div key={f.id} className={`fb-field-row${selectedId === f.id ? ' selected' : ''}`} onClick={() => setSelectedId(f.id)} data-testid={`fb-field-${f.id}`}>
                  <div className="fb-field-tools" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => moveField(f.id, -1)} title="Move up">↑</button>
                    <button onClick={() => moveField(f.id, 1)} title="Move down">↓</button>
                    <button onClick={() => deleteField(f.id)} title="Delete" data-testid={`del-field-${f.id}`}><Trash2 size={10} /></button>
                  </div>
                  <FieldRender field={f} value="" onChange={() => {}} disabled />
                  {selectedId === f.id && <FieldEditor field={f} onChange={(p) => updateField(f.id, p)} />}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'preview' && (
        <div style={{ flex: 1, overflow: 'auto' }}>
          <iframe
            title="form preview"
            srcDoc={`<html><body style="margin:0;padding:0;background:transparent"></body></html>`}
            style={{ display: 'none' }}
          />
          <div className="fp-shell" style={{ minHeight: 'auto' }}>
            <div className="fp-card">
              <div className="fp-title">{draft.title}</div>
              {draft.description && <div className="fp-desc">{draft.description}</div>}
              {draft.fields.map((f) => <FieldRender key={f.id} field={f} value="" onChange={() => {}} disabled />)}
              <button className="fp-submit" disabled type="button">{draft.submit_label || 'Submit'}</button>
              <div className="fp-foot">Powered by LegacyCRM · preview</div>
            </div>
          </div>
        </div>
      )}

      {tab === 'submissions' && (
        <div style={{ flex: 1, overflow: 'auto', padding: 16, background: 'var(--bg-weak)' }} data-testid="submissions-pane">
          {submissions.length === 0 ? (
            <div className="empty-state"><div className="ttl">No submissions</div><div className="sub">Share the public URL or submit internally to see entries here.</div></div>
          ) : (
            <div className="card">
              <table className="tbl">
                <thead><tr><th>When</th><th>Data</th><th>Contact</th></tr></thead>
                <tbody>
                  {submissions.map((s) => (
                    <tr key={s.id}>
                      <td>{new Date(s.submitted_at).toLocaleString()}</td>
                      <td><pre style={{ fontSize: 10, margin: 0, fontFamily: 'monospace' }}>{JSON.stringify(s.data, null, 2)}</pre></td>
                      <td>{s.contact_id ? <span className="tag-pill ok">Contact created</span> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'settings' && (
        <div style={{ flex: 1, overflow: 'auto', padding: 16, background: 'var(--bg-weak)' }} data-testid="form-settings-pane">
          <div className="feature-card">
            <div className="ttl">Form basics</div>
            <div className="grid">
              <div><label className="f-label">Title</label><input className="f-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="fs-title" /></div>
              <div><label className="f-label">Submit button label</label><input className="f-input" value={draft.submit_label || ''} onChange={(e) => setDraft({ ...draft, submit_label: e.target.value })} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label className="f-label">Description</label><textarea className="f-textarea" rows={2} value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label className="f-label">Success message</label><textarea className="f-textarea" rows={2} value={draft.success_message || ''} onChange={(e) => setDraft({ ...draft, success_message: e.target.value })} /></div>
            </div>
            <label className="f-checkbox" style={{ marginTop: 10 }}><input type="checkbox" checked={!!draft.is_active} onChange={() => setDraft({ ...draft, is_active: !draft.is_active })} data-testid="fs-active" />Form is live (accepting submissions)</label>
          </div>

          {draft.type === 'lead' && (
            <div className="feature-card">
              <div className="ttl"><ExternalLink size={12} /> Public URL</div>
              <div className="sub">Share this link wherever you advertise. Submissions create contacts automatically.</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <input className="f-input" readOnly value={publicUrl || ''} onFocus={(e) => e.target.select()} data-testid="fs-public-url" />
                <button className="btn-secondary" onClick={() => navigator.clipboard.writeText(publicUrl)} type="button">Copy</button>
                <a className="btn-primary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }} href={publicUrl} target="_blank" rel="noreferrer">Open</a>
              </div>
              <div style={{ marginTop: 12 }}>
                <label className="f-label">Custom slug (optional)</label>
                <input className="f-input" value={draft.public_slug || ''} onChange={(e) => setDraft({ ...draft, public_slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} data-testid="fs-slug" />
              </div>
              <label className="f-checkbox" style={{ marginTop: 10 }}><input type="checkbox" checked={!!draft.auto_create_contact} onChange={() => setDraft({ ...draft, auto_create_contact: !draft.auto_create_contact })} />Auto-create a Contact when someone submits</label>
              <div style={{ marginTop: 8 }}>
                <label className="f-label">Redirect after submit (optional)</label>
                <input className="f-input" value={draft.redirect_url || ''} placeholder="https://example.com/thanks" onChange={(e) => setDraft({ ...draft, redirect_url: e.target.value })} />
              </div>
            </div>
          )}

          <div className="feature-card">
            <div className="ttl">Danger zone</div>
            <button className="btn-danger" type="button" onClick={async () => {
              if (!window.confirm('Delete this form and all submissions?')) return;
              try { await api.delete(`/forms/${form.id}`); onChange(); onClose(); }
              catch (e) { alert(formatApiError(e)); }
            }} data-testid="delete-form-btn"><Trash2 size={11} style={{ marginRight: 4 }} />Delete form</button>
          </div>
        </div>
      )}
    </>
  );
};

const FieldEditor = ({ field, onChange }) => {
  const isOptions = field.type === 'select' || field.type === 'radio';
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '0.5px dashed var(--stroke-light)' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div><label className="f-label">Label</label><input className="f-input" value={field.label} onChange={(e) => onChange({ label: e.target.value })} data-testid={`fld-label-${field.id}`} /></div>
        {field.type !== 'heading' && field.type !== 'divider' && (
          <div>
            <label className="f-label">Map to contact field <span style={{ color: 'var(--text-faded)' }}>(lead forms)</span></label>
            <select className="f-select" value={field.name || ''} onChange={(e) => onChange({ name: e.target.value })}>
              <option value="">— None —</option>
              <option value="first">First name</option>
              <option value="last">Last name</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="company">Company</option>
              <option value="stage">Stage</option>
            </select>
          </div>
        )}
      </div>
      {field.type !== 'heading' && field.type !== 'divider' && (
        <div style={{ marginTop: 8, display: 'flex', gap: 12, alignItems: 'center' }}>
          <input className="f-input" placeholder="Placeholder" value={field.placeholder || ''} onChange={(e) => onChange({ placeholder: e.target.value })} style={{ flex: 1 }} />
          <label className="f-checkbox"><input type="checkbox" checked={!!field.required} onChange={() => onChange({ required: !field.required })} />Required</label>
        </div>
      )}
      {isOptions && (
        <div style={{ marginTop: 8 }}>
          <label className="f-label">Options (one per line)</label>
          <textarea className="f-textarea" rows={3} value={(field.options || []).join('\n')} onChange={(e) => onChange({ options: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })} />
        </div>
      )}
    </div>
  );
};

export default Forms;
